import { Formik, FormikProps } from "formik";
import isEqual from "lodash/isEqual";
import { useContext, useEffect, useRef, useState } from "react";
import AGSelect from "../../../components/AgSelect";
import AGButton from "../../../components/Button";
import AGModal from "../../../components/Modal";
import { compare } from "../../../utils/sort";

import { LoadingIcon } from "../../../assets/svg/loadingIcon";
import { AddIconCurrencyInput, AddIconDatePicker } from "../../../components/AddIconDatePicker";
import AGCurrencyInput from "../../../components/CurrencyInput";
import AGInput from "../../../components/Input";
import { UserContext } from "../../../Provider";
import { editInquirySchema } from "../../../Schema/CommerceCompassSchema";
import { INQUIRY_CONSTANT, INQUIRY_MORE_OPTION, OFFERING_TYPE, OFFERING_TYPE_NAME } from "../../../utils/constant";
import { handleNumericDateInput } from "../../../utils/helper";
import { InquiryBusinessGeneratingType } from "../addInquiryModal/addInquiry.props";
import { EditInquiryModelProps } from "../editInquiryModal/editInquiry.props";
import { TextFull } from "../inboxMessage.styled";
import { CustomBackground, FormContainer, StyledDatePicker } from "./editInquiry.styled";

const EditInquiryModal = ({
  visible,
  toggleModal,
  inquiryDetails,
  isLoadingInquiryDetails,
  inquiryCreatingInfo,
  editInquiry,
}: EditInquiryModelProps) => {
  const user = useContext(UserContext);
  const isAdmin = user?.roles.includes("Admin")
  const formRef = useRef<FormikProps<any>>(null);
  const [businessGeneratingCheckBox, setBusinessGeneratingCheckbox] =
    useState<InquiryBusinessGeneratingType | undefined>(undefined);
  const [initialValues, setInitialValues] = useState<any>();
  const [submitCount, setSubmitCount] = useState(0);
  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);

  const handleClose = () => {
    setInitialValues(undefined);
    toggleModal();
    setSubmitCount(0);
  };
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const isMobileScreen = screenWidth < 769;

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (inquiryDetails !== undefined && inquiryDetails !== null) {
      setInitialValues({
        inboxMessageId: inquiryDetails.inboxMessageId,
        status: {
          value: inquiryDetails.statusType?.statusId,
          label: inquiryDetails.statusType?.status,
        },
        dealValue: inquiryDetails.dealValue,
        closeDate: inquiryDetails.closeDate ? new Date(`${inquiryDetails.closeDate}Z`) : null,
        businessGeneratingTypeId:
          inquiryDetails?.businessGeneratingType?.businessGeneratingTypeId ??
          INQUIRY_CONSTANT.BUSINESS_GENERATING_TYPE_ID.I_DONT_KNOW,
        onetimeFee: inquiryDetails.onetimeFee,
        requestedServices: inquiryDetails.requestedServiceType ? {
          value:
            inquiryDetails.requestedServiceType?.requestedServicesTypeId,
          label:
            inquiryDetails.requestedServiceType?.requestedServicesTypeName,
        } : null,
        recurringFee: inquiryDetails.recurringFee,
        frequencyOfRecurringFee:
          inquiryDetails.frequencyOfRecurringFeeType == null
            ? null
            : {
              value: inquiryDetails.frequencyOfRecurringFeeType.frequencyId,
              label: inquiryDetails.frequencyOfRecurringFeeType.frequencyTypeName,
            },
        commentRemarks: inquiryDetails.commentRemarks,
        inquiryType: inquiryDetails.inquiryType,
        inquiryDate: inquiryDetails.inquiryDate ? new Date(`${inquiryDetails.inquiryDate}Z`) : null,
        typeOfClient:
          inquiryDetails.typeOfClient === null
            ? {
              value: INQUIRY_CONSTANT.INQUIRY_TYPE_OF_CLIENT_ID.UNKNOWN,
              label: INQUIRY_CONSTANT.INQUIRY_TYPE_OF_CLIENT_NAME.UNKNOWN
            }
            : {
              value: inquiryDetails.typeOfClient.typeOfClientId,
              label: inquiryDetails.typeOfClient.typeOfClientName,
            },
      });
      setBusinessGeneratingCheckbox({
        businessGeneratingTypeId:
          inquiryDetails?.businessGeneratingType?.businessGeneratingTypeId ??
          INQUIRY_CONSTANT.BUSINESS_GENERATING_TYPE_ID.I_DONT_KNOW,
        businessGeneratingTypeName:
          inquiryDetails.businessGeneratingType?.businessGeneratingTypeName,
        isCheck: true,
      });
    }
  }, [inquiryDetails]);

  if (!inquiryDetails || !initialValues) {
    return null;
  }
  return (
    <div>
      <AGModal
        toggleModal={handleClose}
        title="Edit Inquiry"
        footer={
          <>
            <AGButton
              type="Primary"
              onClick={() => {
                setSubmitCount(submitCount + 1);
                formRef.current?.submitForm();
              }}
              disabled={!isDataChanged}
            >
              Save
            </AGButton>
            <AGButton
              type="Secondary"
              onClick={() => {
                handleClose();
              }}
            >
              Cancel
            </AGButton>
          </>
        }
        visible={visible}
      >
        <CustomBackground>
          <FormContainer className="flex">
            {
              <Formik
                innerRef={formRef}
                initialValues={initialValues}
                validationSchema={editInquirySchema}
                validateOnChange={submitCount > 0 ? true : false}
                validateOnBlur={false}
                onSubmit={(values) => {
                  let data = {
                    businessGeneratingTypeId: values.businessGeneratingTypeId,
                    closeDate: values.closeDate,
                    CommentRemarks: values.commentRemarks?.trim(),
                    DealValue: values.dealValue,
                    FrequencyOfRecurringFeeId: values.frequencyOfRecurringFee?.value,
                    OnetimeFee: values.onetimeFee,
                    recurringFee: values.recurringFee,
                    statusId: values.status?.value,
                    inboxMessageId: values.inboxMessageId,
                    typeOfClientId: values.typeOfClient?.value,
                    requestedServicesId: values.requestedServices?.value,
                  };
                  setInitialValues(undefined);
                  editInquiry(data);
                }}
              >
                {({ values, errors, setFieldValue, submitForm, validateField }) => {

                  const isDirty = !isEqual(
                    {
                      ...values,
                    },
                    initialValues
                  );

                  setIsDataChanged(isDirty);

                  return (
                    <form
                      onSubmit={(e) => e.preventDefault()}
                      className="form"
                    >
                      {isLoadingInquiryDetails ||
                        inquiryDetails === undefined ||
                        inquiryDetails === null ? (
                        <div className="flex flex-row px-[20px] py-[5px] justify-center border-t-[1px]">
                          <LoadingIcon
                            width={60}
                            height={60}
                          />
                        </div>
                      ) : (
                        <>
                          <div className="w-full">
                            <div className="flex">
                              <div className="label-title">Inquiry Date</div>
                              <span className="required">*</span>
                            </div>
                            <AddIconDatePicker>
                              <StyledDatePicker
                                disabled={true}
                                dateFormat={"MM/dd/yyyy"}
                                maxDate={new Date()}
                                onChange={(e) => { }}
                                selected={values.inquiryDate}
                                placeholderText="mm/dd/yyyy"
                                onKeyDown={handleNumericDateInput}
                              />
                            </AddIconDatePicker>
                          </div>
                          <div className="mb-[1rem] mt-[1rem]">
                            <div className="flex">
                              <div className="label-title">Inquiry Sender Organization</div>
                              <span className="required">*</span>
                            </div>
                            <AGSelect
                              isDisabled={true}
                              value={{
                                value: inquiryDetails.senderFirm?.firmId,
                                label: inquiryDetails.senderFirm?.firmName,
                              }}
                            />
                          </div>
                          <div className="mb-[1rem]">
                            <div className="flex">
                              <div className="label-title">Inquiry Sender</div>
                              <span className="required">*</span>
                            </div>
                            <AGSelect
                              isDisabled={true}
                              value={{
                                value: inquiryDetails.sender?.inquirySenderId,
                                label:
                                  inquiryDetails.sender?.firstName +
                                  " " +
                                  inquiryDetails.sender?.lastName,
                              }}
                            />
                          </div>
                          <div className="mb-[1rem]">
                            <div className="flex">
                              <div className="label-title">Inquiry Receiver Organization</div>
                              <span className="required">*</span>
                            </div>
                            <AGSelect
                              isDisabled={true}
                              value={{
                                value: inquiryDetails.receiverFirm?.firmId,
                                label: inquiryDetails.receiverFirm?.firmName,
                              }}
                            />
                          </div>
                          <div className="mb-[1rem]">
                            <div className="flex">
                              <div className="label-title">Inquiry Receiver</div>
                              <span className="required">*</span>
                            </div>
                            <AGSelect
                              isMultiDisabled={true}
                              isDisabled={true}
                              isMulti
                              isClearable
                              value={inquiryDetails.receivers
                                ?.map((e) => {
                                  return {
                                    value: e.inquiryReceiverId,
                                    label: e.firstName + " " + e.lastName,
                                  };
                                })
                                .sort((a, b) => compare(a, b, "label"))}
                            />
                          </div>
                          <div className="mb-[1rem]">
                            <div className="flex">
                              <div className="label-title">Sender Country</div>
                              <span className="required">*</span>
                            </div>
                            <AGSelect
                              isDisabled
                              value={{
                                value: inquiryDetails.senderFirm?.firmId,
                                label: inquiryDetails.senderFirm?.firmCountry,
                              }}
                            />
                          </div>
                          <div className="mb-[1rem]">
                            <div className="flex">
                              <div className="label-title">Sender Region</div>
                              {/* <span className="required">*</span> */}
                            </div>
                            <AGSelect
                              isDisabled
                              value={{
                                value: inquiryDetails.senderFirm?.firmId,
                                label: inquiryDetails.senderFirm?.firmRegion,
                              }}
                              components={{DropdownIndicator: null}}
                            />
                          </div>
                          <div className="mb-[1rem]">
                            <div className="flex">
                              <div className="label-title">Receiver Country</div>
                              <span className="required">*</span>
                            </div>
                            <AGSelect
                              isDisabled
                              value={{
                                value: inquiryDetails.receiverFirm?.firmId,
                                label: inquiryDetails.receiverFirm?.firmCountry,
                              }}
                            />
                          </div>
                          <div className="mb-[1rem]">
                            <div className="flex">
                              <div className="label-title">Receiver Region</div>
                              {/* <span className="required">*</span> */}
                            </div>
                            <AGSelect
                              isDisabled
                              value={{
                                value: inquiryDetails.receiverFirm?.firmId,
                                label: inquiryDetails.receiverFirm?.firmRegion,
                              }}
                              components={{DropdownIndicator: null}}
                            />
                          </div>
                          <div className="mb-[1rem]">
                            <div className="flex">
                              <div className="label-title">Subject</div>
                              <span className="required">*</span>
                            </div>
                            <TextFull
                              isDisabled={true}
                              dangerouslySetInnerHTML={{
                                __html:
                                  inquiryDetails?.subject
                                    ?.trim()
                                    .replace(/</g, "&lt;")
                                    .replace(/>/g, "&gt;") ?? "",
                              }}
                            />
                          </div>
                          <div className="mb-[1rem]">
                            <div className="flex">
                              <div className="label-title">Message</div>
                              <span className="required">*</span>
                            </div>
                            <TextFull
                              isDisabled={true}
                              dangerouslySetInnerHTML={{
                                __html:
                                  inquiryDetails?.body
                                    ?.trim()
                                    .replace(/</g, "&lt;")
                                    .replace(/>/g, "&gt;")
                                    .replace(/\n/g, "<br />") ?? "",
                              }}
                            />
                          </div>
                          {inquiryDetails.offeringType && (
                            <div className="mb-[1rem]">
                              <div className="flex">
                                <div className="label-title">Offering Type</div>
                              </div>
                              <AGSelect
                                isDisabled
                                value={{
                                  value: inquiryDetails.offeringType.offeringTypeId,
                                  label: inquiryDetails.offeringType.offeringTypeName,
                                }}
                              />
                            </div>
                          )}
                          {inquiryDetails.other && (
                            <div className="mb-[1rem]">
                              <div className="flex">
                                <div className="label-title">Offering Type</div>
                              </div>
                              <AGSelect
                                isDisabled
                                value={{
                                  value: inquiryDetails.other.offeringTypeId,
                                  label: inquiryDetails.other.offeringTypeName,
                                }}
                              />
                            </div>
                          )}
                          {(inquiryDetails.offeringType?.offeringTypeId === OFFERING_TYPE.SERVICE
                            || inquiryDetails.other?.offeringTypeId === OFFERING_TYPE.SERVICE) && (
                              <div className="mb-[1rem]">
                                <div className="flex">
                                  <div className="label-title">Specialized Service</div>
                                  <span className="required">*</span>
                                </div>
                                <AGSelect
                                  isDisabled
                                  value={inquiryDetails.other?.offeringTypeId === OFFERING_TYPE.SERVICE ?
                                    {
                                      value: -1,
                                      label: INQUIRY_MORE_OPTION.OTHER,
                                    }
                                    : {
                                      value: inquiryDetails.mainService?.serivceId,
                                      label: inquiryDetails.mainService?.serviceName,
                                    }}
                                />
                              </div>
                            )}
                          {(inquiryDetails.offeringType?.offeringTypeId === OFFERING_TYPE.PRODUCT
                            || inquiryDetails.other?.offeringTypeId === OFFERING_TYPE.PRODUCT) && (
                              <div className="mb-[1rem]">
                                <div className="flex">
                                  <div className="label-title">Specialized Product</div>
                                  <span className="required">*</span>
                                </div>
                                <AGSelect
                                  isDisabled
                                  value={inquiryDetails.other?.offeringTypeId === OFFERING_TYPE.PRODUCT ?
                                    {
                                      value: -1,
                                      label: INQUIRY_MORE_OPTION.OTHER,
                                    }
                                    : {
                                      value: inquiryDetails.mainService?.serivceId,
                                      label: inquiryDetails.mainService?.serviceName,
                                    }}
                                />
                              </div>
                            )}
                          {(inquiryDetails.offeringType?.offeringTypeId === OFFERING_TYPE.SECONDMENT
                            || inquiryDetails.other?.offeringTypeId === OFFERING_TYPE.SECONDMENT) && (
                              <div className="mb-[1rem]">
                                <div className="flex">
                                  <div className="label-title">Specialized Service</div>
                                  <span className="required">*</span>
                                </div>
                                <AGSelect
                                  isDisabled
                                  value={inquiryDetails.other?.offeringTypeId === OFFERING_TYPE.SECONDMENT ?
                                    {
                                      value: -1,
                                      label: INQUIRY_MORE_OPTION.OTHER,
                                    }
                                    : {
                                      value: inquiryDetails.mainService?.serivceId,
                                      label: inquiryDetails.mainService?.serviceName,
                                    }}
                                />
                              </div>
                            )}
                          {(isAdmin || (inquiryDetails.requestedServiceType && !isAdmin)) && (
                            <div className="mb-[1rem]">
                              <div className="flex">
                                <div className="label-title">Requested Services</div>
                              </div>
                              <AGSelect
                                isDisabled={!isAdmin}
                                placeholder="Select requested service"
                                onChange={(e: any) => {
                                  setFieldValue("requestedServices", e);
                                }}
                                value={values.requestedServices}
                                options={inquiryCreatingInfo?.inquiryRequestedServiceTypes?.map((e) => {
                                  return {
                                    value: e.requestedServicesTypeId,
                                    label: e.requestedServicesTypeName,
                                  };
                                })}
                              />
                            </div>
                          )}
                          <div className="mb-[1rem]">
                            <div className="flex">
                              <div className="label-title">Type of Client</div>
                            </div>
                            <AGSelect
                              value={values.typeOfClient}
                              placeholder="Select type of client"
                              options={
                                (() => {
                                  const specialLabels = ["Other","Unknown"];

                                  const options = inquiryCreatingInfo?.inquiryTypeOfClients?.map((e) => ({
                                    value: e.typeOfClientId,
                                    label: e.typeOfClientName,
                                  })) || [];

                                  const sorted = options.sort((a, b) => a.label.localeCompare(b.label));

                                  const normal = sorted.filter(opt => !specialLabels.includes(opt.label));
                                  const special = sorted.filter(opt => specialLabels.includes(opt.label));

                                  return [...normal, ...special.sort((a,b) => b.label.localeCompare(a.label))];
                                })()
                              }
                              onChange={(e: any) => {
                                setFieldValue("typeOfClient", e);
                              }}
                            />
                          </div>
                          <div className="mb-[1rem]">
                            <div className="flex">
                              <div className="label-title">Status</div>
                            </div>
                            <AGSelect
                              placeholder="Select Status"
                              value={values.status}
                              options={inquiryCreatingInfo?.inquiryStatusTypes
                                .sort((a,b) => a.statusId - b.statusId)
                                .map((e) => {
                                  return {
                                    value: e.statusId,
                                    label: e.status,
                                  };
                                })}
                              onChange={(e: any) => {
                                setFieldValue("status", e);
                              }}
                            />
                          </div>
                          {/* <div className="mb-[1rem]">
                            <div className="flex">
                              <div className="label-title">Deal Value (USD)</div>
                            </div>
                            <AddIconCurrencyInput>
                              <AGCurrencyInput
                                onChange={(e: any) => {
                                  let value = e.target.value;
                                  if (value !== null && value !== "") {
                                    setFieldValue("dealValue", Math.trunc(value * 100) / 100);
                                  } else {
                                    setFieldValue("dealValue", null);
                                  }

                                  setTimeout(() => {
                                    validateField("dealValue");
                                  }, 0);
                                }}
                                value={values.dealValue}
                                placeholder="Enter non-zero deal value (e.g., $10.00)"
                              />
                            </AddIconCurrencyInput>
                            {errors.dealValue && (
                              <div className="error">{String(errors.dealValue)}</div>
                            )}
                          </div> */}

                          {/* <div className="mb-[1rem]">
                            <div className="flex">
                              <div className="label-title">Business Generating?</div>
                            </div>
                            <div className="flex items-center mt-[0.5rem]">
                              {inquiryCreatingInfo?.inquiryBusinessGeneratingTypes.map((x) => (
                                <div
                                  className={`flex items-center ${!isMobileScreen ? "w-1/5" : "w-1/3"
                                    } `}
                                >
                                  <AGRadioButtonV2
                                    checked={
                                      businessGeneratingCheckBox?.businessGeneratingTypeId ===
                                        x.businessGeneratingTypeId
                                        ? businessGeneratingCheckBox?.isCheck
                                        : false
                                    }
                                    onChange={(e) => {
                                      setBusinessGeneratingCheckbox({
                                        businessGeneratingTypeId: x.businessGeneratingTypeId,
                                        businessGeneratingTypeName: x.businessGeneratingTypeName,
                                        isCheck: true,
                                      });
                                      setFieldValue(
                                        "businessGeneratingTypeId",
                                        x.businessGeneratingTypeId,
                                      );
                                    }}
                                  />
                                  <div className="mt-[3px] ml-[10px]">
                                    <span className="service-available-options">
                                      {x.businessGeneratingTypeName}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                            {errors.businessGeneratingTypeId && (
                              <div className="error">This field is required.</div>
                            )}
                          </div> */}
                          {inquiryDetails.offeringType?.offeringTypeId !== OFFERING_TYPE.SECONDMENT && (
                            <div className="mb-[1rem]">
                              <div className="flex">
                                <div className="label-title">One Time Fee (USD)</div>
                              </div>
                              <AddIconCurrencyInput>
                                <AGCurrencyInput
                                  onChange={(e: any) => {
                                    let fee = e.target.value === "" ? null : Number(e.target.value);
                                    if (fee !== null) {
                                      setFieldValue("onetimeFee", Math.trunc(fee * 100) / 100);
                                    } else {
                                      setFieldValue("onetimeFee", null);
                                    }

                                    setTimeout(() => {
                                      validateField("onetimeFee");
                                    }, 0);
                                  }}
                                  value={values.onetimeFee}
                                  placeholder="Enter non-zero one time fee (e.g., $10.00)"
                                />
                              </AddIconCurrencyInput>
                              {errors.onetimeFee && (
                                <div className="error">{String(errors.onetimeFee)}</div>
                              )}
                            </div>
                          )}

                          {inquiryDetails.offeringType?.offeringTypeId !== OFFERING_TYPE.SECONDMENT && (
                            <div className="mb-[1rem]">
                              <div className="flex">
                                <div className="label-title">Recurring Fee (USD)</div>
                              </div>
                              <AddIconCurrencyInput>
                                <AGCurrencyInput
                                  onChange={(e: any) => {
                                    let fee = e.target.value === "" ? null : Number(e.target.value);
                                    if (fee !== null) {
                                      setFieldValue("recurringFee", Math.trunc(fee * 100) / 100);
                                    } else {
                                      setFieldValue("recurringFee", null);
                                    }
                                    if (fee === 0 || fee === null) {
                                      setFieldValue("frequencyOfRecurringFee", null);
                                    }

                                    setTimeout(() => {
                                      validateField("recurringFee");
                                    }, 0);
                                  }}
                                  value={values.recurringFee}
                                  placeholder="Enter non-zero recurring fee (e.g., $10.00)"
                                />
                              </AddIconCurrencyInput>
                              {errors.recurringFee && (
                                <div className="error">{String(errors.recurringFee)}</div>
                              )}
                            </div>
                          )}

                          {inquiryDetails.offeringType?.offeringTypeId !== OFFERING_TYPE.SECONDMENT && (
                            <div className="mb-[1rem]">
                              <div className="flex">
                                <div className="label-title">Frequency of Recurring Fee</div>
                              </div>
                              <AGSelect
                                isClearable
                                placeholder="Select frequency of recurring fee"
                                onChange={(e: any) => {
                                  setFieldValue("frequencyOfRecurringFee", e);
                                }}
                                value={values.frequencyOfRecurringFee}
                                options={inquiryCreatingInfo?.inquiryFrequencyOfRecurringFeeTypes
                                  ?.map((e) => {
                                    return {
                                      value: e.frequencyId,
                                      label: e.frequencyTypeName,
                                    };
                                  })
                                  .sort((a, b) => compare(a, b, "label"))}
                              />
                              {values.recurringFee !== undefined &&
                                (values.recurringFee > 0
                                  ? errors.frequencyOfRecurringFee && (
                                    <div className="error">This field is required.</div>
                                  )
                                  : null)}
                            </div>
                          )}

                          <div className="mb-[1rem]">
                            <div className="flex">
                              <div className="label-title">Close Date</div>
                            </div>
                            <AddIconDatePicker>
                              <StyledDatePicker
                                isClearable
                                onChange={(e: Date | null) => {
                                  if (e != null) {
                                    setFieldValue("closeDate", new Date(e));
                                  } else {
                                    setFieldValue("closeDate", null);
                                  }
                                }}
                                onClickOutside={() => {
                                  if (isMobileScreen) {
                                    setTimeout(() => {
                                      if (document.activeElement instanceof HTMLElement) {
                                        document.activeElement.blur();
                                      }
                                    }, 200)
                                  }
                                }}
                                popperPlacement={`top-start`}
                                dateFormat={"MM/dd/yyyy"}
                                minDate={values.inquiryDate}
                                selected={values.closeDate}
                                placeholderText="mm/dd/yyyy"
                                onKeyDown={handleNumericDateInput}
                              />
                            </AddIconDatePicker>
                          </div>
                          <div className="mb-[1rem]]">
                            <div className="flex">
                              <div className="label-title">Comments/Remarks (including Estimated Fees, if known)</div>
                            </div>
                            <AGInput
                              value={values.commentRemarks}
                              onChange={(e: any) => {
                                setFieldValue("commentRemarks", e.target.value);
                              }}
                              placeholder="Enter comments/remarks"
                              className="w-full"
                            />
                          </div>
                        </>
                      )}
                    </form>
                  );
                }}
              </Formik>
            }
          </FormContainer>
        </CustomBackground>
      </AGModal>
    </div>
  );
};
export default EditInquiryModal;
